import camelCase from 'camelcase';

import type {
    PbAdvanced,
    PbBackground,
    PbBorder,
    PbCssClasses,
    PbisHidden,
    PbMargin,
    PbPadding,
    PbParallaxOptions,
    PbTextAlign,
    PbVideoOptions,
} from '../types/pbShared';
import type { CustomCSSProperties, CustomHtmlElement } from '../types/pbTypes';

/**
 * Parse style string
 * @param styleString
 */
export function parseStyleString(styleString?: string): CustomCSSProperties | undefined {
    if (!styleString) {
        return {};
    }

    const styles: CustomCSSProperties = {};
    const styleArray = styleString.split(';');

    styleArray.forEach((item) => {
        const rule = item.split(':');
        if (rule[1]) {
            const key = camelCase(rule[0].trim());
            const value = rule[1].trim();
            styles[key] = value;
        }
    });

    return styles;
}

/**
 * Get advanced styles
 * @param node
 * @param style
 */
export function getAdvanced(node?: CustomHtmlElement | null, style?: CustomCSSProperties | null): PbAdvanced {
    return {
        ...getBorder(style),
        ...getCssClasses(node),
        ...getMargin(style),
        ...getPadding(style),
        ...getTextAlign(style),
        ...getIsHidden(style),
    };
}

/**
 * Get background images for lozad lazy loading
 * @param node
 */
export function getBackgroundImagesForLozad(node?: CustomHtmlElement | null): {
    desktopImage?: string;
    laptopImage?: string;
    mobileImage?: string;
} {
    if (!node) {
        return {};
    }

    const images = node?.attributes['data-background-images'];

    if (!images) {
        return {};
    }

    try {
        const imagesStructure = JSON.parse(images.replace(/\\"/g, '"'));

        return {
            desktopImage: imagesStructure.desktop_image || undefined,
            mobileImage: imagesStructure.mobile_image || undefined,
            laptopImage: imagesStructure.laptop_image || undefined,
        };
    } catch (error) {
        console.warn('Failed to parse background images:', error);
        return {};
    }
}

/**
 * Get background images
 * @param _node - unused, kept for API compatibility
 * @param style
 */
export function getBackgroundImages(
    _node?: CustomHtmlElement | null,
    style?: CustomCSSProperties | null,
): PbBackground {
    if (!style) {
        return {
            backgroundAttachment: 'scroll',
            backgroundPosition: 'top left',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
        };
    }

    // Return only the CSS background properties, not the image URLs
    // Image URLs are now handled separately via getBackgroundImagesForLozad
    const response = {
        backgroundAttachment: style?.backgroundAttachment ?? 'scroll',
        backgroundColor: style?.backgroundColor,
        backgroundPosition: style?.backgroundPosition ?? 'top left',
        backgroundRepeat: style?.backgroundRepeat ?? 'no-repeat',
        backgroundSize: style?.backgroundSize ?? 'cover',
    };

    return response;
}

/**
 * Get padding
 * @param style
 */
export function getPadding(style?: CustomCSSProperties | null): PbPadding {
    if (!style) {
        return {};
    }

    if (style?.padding) {
        return {
            padding: style?.padding,
        };
    }

    return {
        paddingBottom: style?.paddingBottom,
        paddingLeft: style?.paddingLeft,
        paddingRight: style?.paddingRight,
        paddingTop: style?.paddingTop,
    };
}

/**
 * Get margin
 * @param style
 */
export function getMargin(style?: CustomCSSProperties | null): PbMargin {
    if (!style) {
        return {};
    }

    if (style?.margin) {
        return {
            margin: style?.margin,
        };
    }

    return {
        marginBottom: style?.marginBottom,
        marginLeft: style?.marginLeft,
        marginRight: style?.marginRight,
        marginTop: style?.marginTop,
    };
}

/**
 * Get border
 * @param style
 */
export function getBorder(style?: CustomCSSProperties | null): PbBorder {
    if (!style) {
        return {
            borderStyle: 'none',
        };
    }

    return {
        borderStyle: style?.borderStyle ?? 'none',
        borderColor: style?.borderColor,
        borderWidth: style?.borderWidth,
        borderRadius: style?.borderRadius,
    };
}

/**
 * Get text align
 * @param style
 */
export function getTextAlign(style?: CustomCSSProperties | null): PbTextAlign {
    if (!style) {
        return {
            textAlign: 'inherit',
        };
    }

    switch (style?.textAlign) {
        case 'center':
        case 'right':
            return {
                textAlign: style.textAlign,
            };
        case 'left': {
            return {
                textAlign: 'left',
            };
        }
        default:
            return {
                textAlign: 'inherit',
            };
    }
}

/**
 * Get css classes
 * @param node
 */
export function getCssClasses(node?: CustomHtmlElement | null): PbCssClasses {
    if (!node) {
        return {};
    }

    return {
        cssClasses: node.attributes['class'] ? node.attributes['class'].split(' ') : [],
    };
}

/**
 * Get is hidden
 * @param style
 */
export function getIsHidden(style?: CustomCSSProperties | null): PbisHidden {
    if (!style) {
        return {
            isHidden: false,
        };
    }

    return {
        isHidden: style?.display === 'none',
    };
}

/**
 * Get video options
 * @param node
 */
export function getVideoOptions(node?: CustomHtmlElement | null): PbVideoOptions {
    if (!node) {
        return {};
    }

    const videoOverlayColor = (node.childNodes[0] as CustomHtmlElement)?.attributes['data-video-overlay-color'];

    return {
        videoFallbackSrc: node.attributes['data-video-fallback-src'],
        videoLazyLoading: node.attributes['data-video-lazy-load'] === 'true',
        videoLoop: node.attributes['data-video-loop'] === 'true',
        videoOverlayColor: videoOverlayColor,
        videoPlayOnlyVisible: node.attributes['data-video-play-only-visible'] === 'true',
        videoSrc: node.attributes['data-video-src'],
    };
}

/**
 * Get parallax options
 * @param node
 */
export function getParallaxOptions(node?: CustomHtmlElement | null): PbParallaxOptions {
    if (!node) {
        return {
            enableParallax: false,
            parallaxSpeed: 0.5,
        };
    }

    return {
        enableParallax: node.attributes['data-enable-parallax'] === '1',
        parallaxSpeed: parseFloat(node.attributes['data-parallax-speed']) ?? '0.5',
    };
}

/**
 * Get heading type options
 * @param type
 */
export function getHeadingType(type?: string | null): 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | undefined {
    switch (type) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
            return type;
        default:
            return undefined;
    }
}

/**
 * Determine the button type based on class
 * @param className
 */
export function getButtonType(className?: string | null): 'primary' | 'secondary' | 'reset' {
    if (className?.includes('pagebuilder-button-secondary')) {
        return 'secondary';
    }

    if (className?.includes('pagebuilder-button-link')) {
        return 'reset';
    }

    return 'primary';
}

/**
 * Get buttons appearance type
 * @param type
 */
export function getButtonsAppearance(type?: string | null): 'inline' | 'stacked' {
    switch (type) {
        case 'stacked':
            return 'stacked';
        default:
            return 'inline';
    }
}

/**
 * Get button link type
 * @param type
 */
export function getButtonLinkType(type?: string | null): 'product' | 'url' | 'page' | 'category' | undefined {
    switch (type) {
        case 'default':
            return 'url';
        case 'product':
        case 'page':
        case 'category':
            return type;
        default:
            return undefined;
    }
}

/**
 * Get tab navigation alignment
 * @param type
 */
export function getTabNavigationAlignment(type?: string | null): 'left' | 'center' | 'right' {
    const tabAlign = type?.split(' ')?.find((item) => item.startsWith('tab-align-'));

    switch (tabAlign) {
        case 'tab-align-center':
            return 'center';
        case 'tab-align-right':
            return 'right';
        default:
            return 'left';
    }
}
